import { useCallback } from "react";

import { EmptystateStacksColored } from "components/icons/generated";
import TablePagination from "components/Table/Pagination";
import usePagination from "components/Table/Pagination/usePagination";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import TableSimple from "ds/components/TableSimple";
import Tooltip from "ds/components/Tooltip";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { pluralizeNext } from "shared/Pluralize";
import { AccountContext } from "views/AccountWrapper";

import OpenTofuMigrationViewHeader from "../ViewHeader";
import { OPEN_TOFU_MIGRATION_INITIAL_LIMIT, OPEN_TOFU_MIGRATION_TITLE } from "../constants";
import {
  READY_TO_MIGRATE_FILTERS_ORDER_SETTINGS_KEY,
  READY_TO_MIGRATE_LIMIT_STORAGE_KEY,
} from "./constants";
import useOpenTofuMigrationSearchReadyStacks from "./useSearchStacks";
import useOpenTofuMigrationSearchReadySuggestions from "./useSearchSuggestions";
import OpenTofuMigrationReadyToMigrateFiltersLayout from "./FiltersLayout";
import { showOpenTofuMigrationMigrateConfirmationModal } from "./MigrateConfirmationModal";
import { READY_TO_MIGRATE_TABLE_CELLS } from "./Table/Cells";
import { READY_TO_MIGRATE_SORT_CONFIG } from "./Table/constants";
import useOpenTofuMigrationReadyToMigrateTableStore from "./Table/useTableStore";

const disableStickyRight = () => false;

const OpenTofuMigrationReadyToMigrate = () => {
  const { accountName } = useTypedContext(AccountContext);

  useTitle(`${OPEN_TOFU_MIGRATION_TITLE} · ${accountName}`);

  const {
    entities,
    error,
    loadingContent,
    loadPage,
    predicates,
    fullTextSearch,
    endCursor,
    startCursor,
    isPageEmpty,
    hasNoFilteringResults,
    paginationResetKey,
  } = useOpenTofuMigrationSearchReadyStacks();

  const {
    filters,
    filtersMap,
    sortOptions,
    loading: filtersLoading,
    error: filtersError,
    filteredCount,
  } = useOpenTofuMigrationSearchReadySuggestions();

  const {
    rowsPerPage,
    setRowsPerPage,
    from,
    to,
    lastPage,
    page,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
  } = usePagination({
    fetchItems: loadPage,
    endCursor,
    startCursor,
    allItemsCount: filteredCount,
    visibleItemsCount: entities.length,
    initialLimit: OPEN_TOFU_MIGRATION_INITIAL_LIMIT,
    storageKey: READY_TO_MIGRATE_LIMIT_STORAGE_KEY,
    resetKey: paginationResetKey,
    loading: loadingContent,
  });

  const handleMigrate = useCallback(
    () =>
      showOpenTofuMigrationMigrateConfirmationModal({
        predicates,
        fullTextSearch,
        matchingCount: filteredCount,
      }),
    [predicates, fullTextSearch, filteredCount]
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  const isMigrateDisabled = filteredCount === 0 || !!error || !!filtersError;

  let migrateDisabledTooltip = "";
  if (error || filtersError) {
    migrateDisabledTooltip = "The list of stacks could not be loaded.";
  } else if (filteredCount === 0) {
    migrateDisabledTooltip = "No stacks match the current filters.";
  }

  return (
    <>
      <OpenTofuMigrationViewHeader>
        <Tooltip
          active={isMigrateDisabled}
          widthMode="maxWidthSm"
          on={(props) => (
            <Button
              {...props}
              variant="primary"
              onPress={handleMigrate}
              disabled={isMigrateDisabled}
            >
              {`Migrate ${pluralizeNext(filteredCount, "matching stack")}`}
            </Button>
          )}
        >
          {migrateDisabledTooltip}
        </Tooltip>
      </OpenTofuMigrationViewHeader>

      <OpenTofuMigrationReadyToMigrateFiltersLayout
        isPageEmpty={isPageEmpty}
        filters={filters}
        sortOptions={sortOptions}
        filtersMap={filtersMap}
        filtersLoading={filtersLoading}
      >
        {isPageEmpty && (
          <EmptyState
            title="Nothing ready to migrate"
            icon={EmptystateStacksColored}
            caption="Stacks eligible for migration will show up here."
          />
        )}
        {hasNoFilteringResults && (
          <EmptyState
            title="No results"
            icon={EmptystateStacksColored}
            caption="Try different filters."
            announce
          />
        )}

        {(!!entities.length || loadingContent) && (
          <>
            <TableSimple
              data={entities}
              loading={loadingContent}
              store={useOpenTofuMigrationReadyToMigrateTableStore}
              ariaLabel="Stacks ready to migrate to OpenTofu"
              cellPerColumn={READY_TO_MIGRATE_TABLE_CELLS}
              filtersStorageKey={READY_TO_MIGRATE_FILTERS_ORDER_SETTINGS_KEY}
              sortConfig={READY_TO_MIGRATE_SORT_CONFIG}
              hasPagination
              hideSettings
              stickyRight={disableStickyRight}
            />
            <TablePagination
              from={from}
              to={to}
              loading={loadingContent}
              goToNextPage={goToNextPage}
              setRowsPerPage={setRowsPerPage}
              goToPrevPage={goToPrevPage}
              goToFirstPage={goToFirstPage}
              goToLastPage={goToLastPage}
              rowsPerPage={rowsPerPage}
              itemsCount={filteredCount}
              lastPage={lastPage}
              page={page}
            />
          </>
        )}
      </OpenTofuMigrationReadyToMigrateFiltersLayout>
    </>
  );
};

export default OpenTofuMigrationReadyToMigrate;
